import { useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';

import { DEMO_SOURCES } from './feed-onboarding.config';

const LEAD_IN_MS = 700;
const TAP_MS = 500;
const LINE_MS = 700;
const LOAD_MS = 1300;
const SETTLE_MS = 900;
const HOLD_MS = 1800;

const SOURCE_MS = TAP_MS + LINE_MS + LOAD_MS + SETTLE_MS;
const CYCLE_MS = LEAD_IN_MS + DEMO_SOURCES.length * SOURCE_MS + HOLD_MS;

const sourceStartMs = (index: number) => LEAD_IN_MS + index * SOURCE_MS;

const BOUNDARIES_MS = [
  ...DEMO_SOURCES.flatMap((_, index) => {
    const start = sourceStartMs(index);
    return [
      start,
      start + TAP_MS,
      start + TAP_MS + LINE_MS,
      start + TAP_MS + LINE_MS + LOAD_MS,
    ];
  }),
  CYCLE_MS,
];

export type DemoCard = {
  key: string;
  sourceIndex: number;
  metaWidth: string;
  titleWidth: string;
  isLoading: boolean;
};

const useDemoClock = () => {
  const prefersReducedMotion = useReducedMotion();
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const nextMs = BOUNDARIES_MS.find((ms) => ms > elapsedMs) ?? CYCLE_MS;
    const timer = setTimeout(
      () => setElapsedMs(nextMs >= CYCLE_MS ? 0 : nextMs),
      nextMs - elapsedMs
    );
    return () => clearTimeout(timer);
  }, [elapsedMs, prefersReducedMotion]);

  return prefersReducedMotion ? CYCLE_MS - 1 : elapsedMs;
};

export const useFeedDemo = () => {
  const elapsedMs = useDemoClock();

  const sources = DEMO_SOURCES.map((_, index) => {
    const sinceStartMs = elapsedMs - sourceStartMs(index);
    const isActive = sinceStartMs >= 0 && sinceStartMs < SOURCE_MS - SETTLE_MS;
    return {
      isActive,
      isTapped: isActive && sinceStartMs < TAP_MS,
      isLineDrawn: sinceStartMs >= TAP_MS,
      isFlowing: isActive && sinceStartMs >= TAP_MS + LINE_MS,
      isDone: sinceStartMs >= SOURCE_MS - SETTLE_MS,
    };
  });

  const cards: DemoCard[] = DEMO_SOURCES.map((source, sourceIndex) => {
    const sinceStartMs = elapsedMs - sourceStartMs(sourceIndex);
    if (sinceStartMs < TAP_MS + LINE_MS) return [];
    return source.cards.map((card, cardIndex) => ({
      key: `${sourceIndex}-${cardIndex}`,
      sourceIndex,
      ...card,
      isLoading: !sources[sourceIndex].isDone,
    }));
  }).flat();

  return { sources, cards };
};
